<template>
	<div class="fd-symbolic">
		<select v-model="edge">
			<option value="">-</option>
			<option value="NOW">{{ env.msg("date.picker.now") }}</option>
			<option value="START">{{ env.msg("date.picker.beginning") }}</option>
			<option value="END">{{ env.msg("date.picker.end") }}</option>
		</select>
		<select v-model="range" :disabled="noRangeShiftNeeded">
			<option value="">-</option>
			<option value="D">{{ env.msg("date.picker.day") }}</option>
			<option value="WM">{{ env.msg("date.picker.weekFromMonday") }}</option>
			<option value="WS">{{ env.msg("date.picker.weekFromSunday") }}</option>
			<option value="M">{{ env.msg("date.picker.month") }}</option>
			<option value="Q">{{ env.msg("date.picker.quarter") }}</option>
			<option value="Y">{{ env.msg("date.picker.year") }}</option>
		</select>
		<label>{{ env.msg("date.picker.shift") }}: <input type="number" v-model="shift" :disabled="noRangeShiftNeeded"></label>
	</div>
</template>

<script setup lang="ts">
import {injectEnv} from "../../Environment";
import {SYMBOLIC_LOOSE_REGEX, SYMBOLIC_REGEX} from "./Temporal";

import {computed} from "vue";

const env = injectEnv();

const props = defineProps({
	modelValue: String
});
const emit = defineEmits(['update:modelValue']);

export interface ParsedSymbolic {
	valid: boolean,
	edge?: "NOW" | "START" | "END" | ""
	range?: "D" | "WM" | "WS" | "M" | "Q" | "Y"
	shift: number
}

const parsedValue = computed<ParsedSymbolic>({
	get(): ParsedSymbolic {
		return parse(props.modelValue);
	},
	set(v: ParsedSymbolic): void {
		const fmt = format(v);
		emit("update:modelValue", fmt);
	}
});

const edge = computed<string>({
	get(): string {
		const pv = parsedValue.value;
		return !pv.valid || pv.edge == null ? "" : pv.edge;
	},
	set(v: string): void {
		const pv = parsedValue.value;
		// @ts-ignore
		pv.edge = v;
		parsedValue.value = validate(pv);
	}
});

const range = computed<string>({
	get(): string {
		const pv = parsedValue.value;
		return !pv.valid || pv.range == null ? "" : pv.range;
	},
	set(v: string): void {
		const pv = parsedValue.value;
		// @ts-ignore
		pv.range = v;
		parsedValue.value = validate(pv);
	}
});

const shift = computed<number>({
	get(): number {
		const pv = parsedValue.value;
		return !pv.valid ? 0 : pv.shift;
	},
	set(v: number): void {
		const pv = parsedValue.value;
		// @ts-ignore
		pv.shift = v;
		parsedValue.value = validate(pv);
	}
});

const noRangeShiftNeeded = computed<boolean>(() => {
	const pv = parsedValue.value;
	return pv.valid && (pv.edge === "NOW" || pv.edge === "");
});


function format(s: ParsedSymbolic): string {
	const f = format2(s);
	console.log("Format " + JSON.stringify(s) + " -> " + f);
	return f;
}

function format2(s: ParsedSymbolic): string {
	if (s.edge == null || s.edge === "") return "";
	let f = "";
	if (s.edge === "NOW") return "NOW()";
	if (s.range) f += s.range;
	f += s.edge;
	f += "(" + s.shift + ")";
	return f;
}

function parse(value: string | undefined): ParsedSymbolic {
	if (value != null) {
		let data = value.toUpperCase().trim();
		if (data.length === 0) return {valid: true, edge: "", shift: 0};  // empty is valid
		if (SYMBOLIC_REGEX.test(data)) {
			const res: ParsedSymbolic = {valid: true, shift: 0};
			const pidx = data.indexOf("(");
			let sym = data.substring(0, pidx);
			let shift = data.substring(pidx + 1, data.indexOf(")"));
			// @ts-ignore
			if ("NOW" === sym) {
				res.edge = sym;
			} else {
				// For compatibility reasons
				if (sym === "START") sym = "MSTART";
				else if (sym === "END") sym = "MEND";

				const twoCharPrfx = sym.startsWith("W");
				// @ts-ignore
				res.range = sym.substring(0, twoCharPrfx ? 2 : 1);
				// @ts-ignore
				res.edge = sym.substring(twoCharPrfx ? 2 : 1);
				if (shift.length > 0) res.shift = parseInt(shift);
			}
			return res;
		} else if (SYMBOLIC_LOOSE_REGEX.test(data)) {
			const res: ParsedSymbolic = {valid: false, shift: 0};
			const pidx = data.indexOf("(");
			let sym = data.substring(0, pidx).toUpperCase();
			let shift = data.substring(pidx + 1, data.indexOf(")"));
			// @ts-ignore
			if ("NOW" === sym) {
				res.edge = sym;
			} else {
				// For compatibility reasons
				if (sym === "START") sym = "MSTART";
				else if (sym === "END") sym = "MEND";

				if (sym.startsWith("D")) res.range = "D";
				else if (sym.startsWith("WM")) res.range = "WM";
				else if (sym.startsWith("WS")) res.range = "WS";
				else if (sym.startsWith("M")) res.range = "M";
				else if (sym.startsWith("Q")) res.range = "Q";
				else if (sym.startsWith("Y")) res.range = "Y";
				if (sym.endsWith("START")) res.edge = "START";
				else if (sym.endsWith("END")) res.edge = "END";
			}
			if (shift.length > 0) res.shift = parseInt(shift);
			return res;
		} else console.log("Data " + data + " didn't pass REGEX test ");
	}
	return {valid: false, shift: 0};
}

/** Auto-correct and set valid flag */
function validate(s: ParsedSymbolic): ParsedSymbolic {
	let valid = false;
	if (s.edge === "NOW" || s.edge === "") valid = true;
	else if (s.edge === "START" || s.edge === "END") {
		valid = s.range === "D" || s.range === "WM" || s.range === "WS" || s.range === "M" || s.range === "Q" || s.range === "Y";
	}
	s.valid = valid;
	return s;
}

</script>

<style>
.fd-symbolic {
	display: flex;
	flex-direction: column;
	row-gap: 4px;
	padding: 8px;
}
</style>